import React, { createContext, useEffect, useReducer } from "react";
import { useHistory } from "react-router-dom";
import jwtDecode from "jwt-decode";
import SplashScreen from "src/components/SplashScreen";
import axios from "axios";
import { gql, useMutation, useQuery } from "@apollo/client";
import {
  SEND_ADMIN_OTP,
  SETUP_ADMIN_OTP,
  VERIFY_ADMIN_OTP,
  VERIFY_ADMIN_BACKUP_CODE,
  USE_ADMIN_OTP_BYPASS,
} from "src/gql/admin";


const initialAuthState = {
  isAuthenticated: false,
  isInitialised: false,
  otpPending: false,
  otpEnabled: false,
  maskedMobile: null,
  user: null,
  otpSendFailed: false,
  otpSendError: null,
};

const reducer = (state, action) => {
  switch (action.type) {
    case "INITIALISE": {
      const { isAuthenticated, user, otpPending, otpEnabled } = action.payload;
      return {
        ...state,
        isAuthenticated,
        isInitialised: true,
        otpPending: otpPending || false,
        otpEnabled: otpEnabled || false,
        user,
      };
    }
    case "LOGIN": {
      return {
        ...state,
        isAuthenticated: true,
        isInitialised: true,
        otpPending: false,
        otpEnabled: false,
        maskedMobile: null,
        user: action.payload.user,
      };
    }
    case "OTP_PENDING": {
      return {
        ...state,
        isAuthenticated: true,
        isInitialised: true,
        otpPending: true,
        otpEnabled: action.payload.otpEnabled,
        maskedMobile: action.payload.maskedMobile || null,
        user: action.payload.user,
      };
    }
    case "OTP_SETUP_DONE": {
      return {
        ...state,
        otpEnabled: true,
        maskedMobile: action.payload.maskedMobile,
      };
    }
    case "OTP_VERIFIED": {
      return {
        ...state,
        otpPending: false,
        otpEnabled: true,
        otpSendFailed: false,
        otpSendError: null,
      };
    }
    case "OTP_SEND_FAILED": {
      return {
        ...state,
        otpSendFailed: true,
        otpSendError: action.payload.error,
      };
    }
    case "CLEAR_OTP_SEND_FAILED": {
      return {
        ...state,
        otpSendFailed: false,
        otpSendError: null,
      };
    }
    case "LOGOUT": {
      return {
        ...state,
        isAuthenticated: false,
        otpPending: false,
        otpEnabled: false,
        maskedMobile: null,
        user: null,
      };
    }
    default:
      return { ...state };
  }
};

const isValidToken = (accessToken) => {
  if (!accessToken) return false;
  const decoded = jwtDecode(accessToken);
  return decoded.exp > Date.now() / 1000;
};

const setSession = (accessToken) => {
  if (accessToken) {
    localStorage.setItem("accessToken", accessToken);
  } else {
    localStorage.removeItem("accessToken");
  }
};

const saveOtpFlags = (otpPending, otpEnabled) => {
  if (otpPending) {
    localStorage.setItem("otp_pending", "true");
    localStorage.setItem("otp_enabled", otpEnabled ? "true" : "false");
  } else {
    localStorage.removeItem("otp_pending");
    localStorage.removeItem("otp_enabled");
  }
};

const LOGIN = gql`
  mutation LoginAdmin($input: UserLogIn) {
    loginAdmin(input: $input) {
      accessToken
      otpRequired
      otpEnabled
      maskedMobile
      userAccount {
        _id
        fname
        lname
        email
        role
        status
        created_at
        updated_at
      }
    }
  }
`;

const ME = gql`
  query {
    me {
      _id
      fname
      lname
      email
      role
      status
      created_at
      updated_at
    }
  }
`;

const AuthContext = createContext({
  ...initialAuthState,
  method: "JWT",
  login: () => Promise.resolve(),
  logout: () => {},

  sendOtp: () => Promise.resolve(),
  setupOtp: () => Promise.resolve(),
  confirmOtpSetup: () => {},
  verifyOtp: () => Promise.resolve(),
  completeOtpVerified: () => {},
  verifyBackupCode: () => Promise.resolve(),
  useBypassToken: () => Promise.resolve(),
});

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialAuthState);

  const [loginAdmin] = useMutation(LOGIN);
  const [sendAdminOtpMutation] = useMutation(SEND_ADMIN_OTP);
  const [setupAdminOtpMutation] = useMutation(SETUP_ADMIN_OTP);
  const [verifyAdminOtpMutation] = useMutation(VERIFY_ADMIN_OTP);
  const [verifyAdminBackupCodeMutation] = useMutation(VERIFY_ADMIN_BACKUP_CODE);
  const [useAdminOtpBypassMutation] = useMutation(USE_ADMIN_OTP_BYPASS);

  const { loading, error, data } = useQuery(ME);

  const login = async (email, password) => {
    const res = await loginAdmin({
      variables: { input: { mobile: email, password } },
    });
    const payload = res.data.loginAdmin;

    setSession(payload.accessToken);

    if (!payload.otpRequired) {
      saveOtpFlags(false, false);
      dispatch({ type: "LOGIN", payload: { user: payload.userAccount } });
      return { redirectTo: null };
    }

    saveOtpFlags(true, payload.otpEnabled);
    dispatch({
      type: "OTP_PENDING",
      payload: {
        otpEnabled: payload.otpEnabled,
        maskedMobile: payload.maskedMobile,
        user: payload.userAccount,
      },
    });

    if (payload.otpEnabled) {
      try {
        await sendAdminOtpMutation();
      } catch (err) {
        dispatch({
          type: "OTP_SEND_FAILED",
          payload: { error: err.message || "Failed to send OTP" },
        });
      }
    }

    return { redirectTo: payload.otpEnabled ? "/auth/otp" : "/auth/otp-setup" };
  };

  /**
   * @param {string} mobile – full format, e.g. "+94771234567"
   * @returns {{ backupCodes: string[] }} – show once to user
   */
  const setupOtp = async (mobile) => {
    const res = await setupAdminOtpMutation({
      variables: { mobile },
    });
    const { backupCodes, maskedMobile } = res.data.setupAdminOtp;
    return { backupCodes, maskedMobile };
  };

  const confirmOtpSetup = (maskedMobile) => {
    saveOtpFlags(true, true);
    dispatch({ type: "OTP_SETUP_DONE", payload: { maskedMobile } });
  };

  const sendOtp = async () => {
    await sendAdminOtpMutation();
  };


  /**
   * @param {string} code
   * @param {{ skipDispatch?: boolean }} opts
   */
  const verifyOtp = async (code, { skipDispatch = false } = {}) => {
    const res = await verifyAdminOtpMutation({ variables: { code } });
    const { success } = res.data.verifyAdminOtp;
    if (success) {
      saveOtpFlags(false, false);
      if (!skipDispatch) {
        dispatch({ type: 'OTP_VERIFIED' });
      }
    }
    return { success };
  };

  const completeOtpVerified = () => {
    dispatch({ type: 'OTP_VERIFIED' });
  };

  /**
   * @returns {{ success: boolean, remainingBackupCodes: number }}
   */
  const verifyBackupCode = async (backupCode) => {
    const res = await verifyAdminBackupCodeMutation({
      variables: { backupCode },
    });
    const { success, remainingBackupCodes } = res.data.verifyAdminBackupCode;
    if (success) {
      saveOtpFlags(false, false);
      dispatch({ type: "OTP_VERIFIED" });
    }
    return { success, remainingBackupCodes };
  };

  const useBypassToken = async (bypassToken) => {
    const res = await useAdminOtpBypassMutation({ variables: { bypassToken } });
    const { success } = res.data.useAdminOtpBypass;
    if (success) {
      saveOtpFlags(false, false);
      dispatch({ type: "OTP_VERIFIED" });
    }
    return { success };
  };

  const logout = () => {
    setSession(null);
    saveOtpFlags(false, false);
    dispatch({ type: "LOGOUT" });
  };

  const clearOtpSendFailed = () => {
    dispatch({ type: "CLEAR_OTP_SEND_FAILED" });
  };

  useEffect(() => {
    if (loading === false && error == null && data) {
      const otpPending = localStorage.getItem("otp_pending") === "true";
      const otpEnabled = localStorage.getItem("otp_enabled") === "true";

      if (otpPending) {
        dispatch({
          type: "INITIALISE",
          payload: {
            isAuthenticated: true,
            user: data.me,
            otpPending: true,
            otpEnabled,
          },
        });
      } else {
        dispatch({
          type: "INITIALISE",
          payload: { isAuthenticated: true, user: data.me },
        });
      }
    } else if (loading === false && error) {
      saveOtpFlags(false, false);
      dispatch({
        type: "INITIALISE",
        payload: { isAuthenticated: false, user: null },
      });
    }
  }, [loading, error, data]);

  if (!state.isInitialised) {
    return <SplashScreen />;
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: "JWT",
        login,
        logout,
        sendOtp,
        setupOtp,
        confirmOtpSetup,
        verifyOtp,
        completeOtpVerified,
        verifyBackupCode,
        useBypassToken,
        clearOtpSendFailed,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
